/**
 * The court replies (Stage 4), in words — one place, as `period.ts` is for
 * periods and `drafting.ts` is for the waiting posture.
 *
 * A reply to a sixty-two-paragraph rejoinder is written in the background over
 * about twelve minutes. The page says what is happening in the office's own
 * words, from what the server reports and nothing else: the state, the counts
 * and the blocks' own states all arrive on the version the workbench already
 * polls (`drafting_usage.court`). **Nothing here works out what is possible or
 * how far a job has got** — it turns the server's facts into sentences.
 *
 * Kept pure, so every sentence an officer reads is pinned in `court.test.ts`.
 */

import type {
  BlockFindingView,
  BlockView,
  CourtDraftView,
  CourtLeftOutView,
  CourtPaperView,
  SourceFileView,
} from '../api/client'

/* ------------------------------------------------------------ the four kinds */

/**
 * What a court paper is, as the office says it at upload.
 *
 * The keys are the server's (`domain/paper_kinds.COURT_KINDS`); the words are
 * this office's, from its own working procedure (`docs/STAGE4_SOP.md` §1) — the
 * office already splits a bundle into Body and Annexures, and calls its own
 * file the POV document.
 */
export const COURT_KINDS = [
  {
    key: 'pleading-body',
    label: 'Body of the pleading (the OA, rejoinder or writ petition itself)',
    short: 'Body of the pleading',
  },
  {
    key: 'pleading-annexures',
    label: 'Annexures filed with it',
    short: 'Annexures filed with the pleading',
  },
  {
    key: 'pov-document',
    label: 'The office’s own file (POV document)',
    short: 'The office’s own file (POV document)',
  },
  {
    key: 'filed-document',
    label: 'A document already filed by the department',
    short: 'Filed by the department',
  },
] as const

export type CourtKind = (typeof COURT_KINDS)[number]['key']

/** The documents of the Tribunal and High Court stage, by register id — never
 *  by title. One list, for every screen that asks "is this a court document?". */
const COURT_DOCUMENTS = new Set(['D18', 'D19', 'D20', 'D21'])
export const isCourtDocument = (code: string | null | undefined): boolean =>
  COURT_DOCUMENTS.has((code ?? '').toUpperCase())

/** The two kinds that belong to a pleading, so the upload asks which one. */
export const belongsToPleading = (kind: string): boolean =>
  kind === 'pleading-body' || kind === 'pleading-annexures'

/**
 * The pleadings a Body or a file of Annexures can belong to.
 *
 * `words` is written into the upload's description, and the server reads which
 * pleading a file belongs to from exactly those words (`court_papers.
 * pleading_named`). They are English on purpose: the screen is English, and
 * the description is what the papers list shows the officer afterwards.
 */
/**
 * How the office uploads the papers of a court case, shown wherever they are
 * uploaded (agreed 18 Oct 2026, at the release).
 *
 * The numbers are the build's, not a rule of court. A file is read about a
 * hundred pages a minute, and one of 199 pages has been read whole; but **every
 * paper of a case is sent to the model together** when a reply is drafted, so
 * the whole case is the real limit — the server refuses above
 * `court_block_token_limit` (700,000 tokens, roughly 750 pages of Hindi or
 * 1,000 of English) and nothing is spent. 600 pages leaves room under it.
 * Splitting a file does not raise that limit; leaving papers out does.
 */
export const COURT_UPLOAD_GUIDE = [
  'Upload each pleading as two files: the body and the annexures.',
  'Keep each file under 200 pages; split a larger annexure bundle at an annexure, as “Annexures part 1”, “part 2”.',
  'Leave out the vakalatnama, identity documents, fee receipts and blank pages.',
  'Keep a whole case under about 600 pages.',
] as const

export const PLEADINGS = [
  { key: 'oa', label: 'Original Application (OA)', words: 'Original Application' },
  { key: 'rejoinder', label: 'Rejoinder', words: 'Rejoinder' },
  { key: 'writ', label: 'Writ petition', words: 'Writ Petition' },
  {
    key: 'order',
    label: 'Order of the Tribunal',
    words: 'Order of the Tribunal',
    bodyOnly: true,
  },
] as const

export type PleadingKey = (typeof PLEADINGS)[number]['key']

/** The pleading each of the three documents answers. A register id, not a title. */
const ANSWERED_PLEADING: Record<string, PleadingKey> = {
  D18: 'oa',
  D19: 'rejoinder',
  D20: 'writ',
}

export const answeredPleading = (documentType: string | undefined): PleadingKey =>
  ANSWERED_PLEADING[(documentType ?? '').toUpperCase()] ?? 'oa'

/** Which pleading an upload's description names — the words `uploadDescription`
 *  writes — or null where it names none. */
export function pleadingOfDescription(
  description: string | null | undefined,
): PleadingKey | null {
  const text = description ?? ''
  const found = [...PLEADINGS]
    .reverse()
    .find((entry) => text.toLowerCase().includes(entry.words.toLowerCase()))
  return found ? found.key : null
}

/** What the upload is called on the papers list — and what tells the server
 *  which pleading a Body or a file of Annexures belongs to. */
export function uploadDescription(
  kind: CourtKind,
  pleading: PleadingKey,
  filedTitle = '',
): string {
  if (kind === 'pov-document') return 'POV document — the office’s own file'
  if (kind === 'filed-document') {
    return filedTitle
      ? `As filed by the department — ${filedTitle}`
      : 'As filed by the department'
  }
  const entry = PLEADINGS.find((p) => p.key === pleading) ?? PLEADINGS[0]
  if (entry.key === 'order') return entry.words
  return `${entry.words} — ${kind === 'pleading-body' ? 'Body' : 'Annexures'}`
}

/** What a file on the case is, in the office's words. */
export function fileKindWords(file: Pick<SourceFileView, 'paper_kind'>): string | null {
  const kind = file.paper_kind ?? ''
  const court = COURT_KINDS.find((k) => k.key === kind)
  if (court) return court.short
  if (kind === 'signed-copy') return 'Signed or filed copy of the app’s own draft'
  return null
}

/**
 * Where the reading of a file has got to.
 *
 * `read_state` is the server's: QUEUED, CHUNKING and OCR while the pages are
 * being turned into text, REVIEW_READY when they all are, FAILED with the
 * worker's own sentence. A Word file's text arrives with it.
 */
export function fileReadingWords(
  file: Pick<
    SourceFileView,
    | 'read_state'
    | 'read_pages_done'
    | 'read_pages_total'
    | 'read_error'
    | 'text_origin'
    | 'ingestion_job_id'
  >,
): { words: string; tone: 'good' | 'waiting' | 'bad' } {
  if (file.text_origin === 'DOCX_UPLOAD') return { words: 'Read', tone: 'good' }
  if (!file.ingestion_job_id) {
    return { words: 'No text can be read from this file', tone: 'bad' }
  }
  const state = file.read_state ?? ''
  if (state === 'FAILED') {
    return {
      words: `Could not be read${file.read_error ? `: ${file.read_error}` : ''}`,
      tone: 'bad',
    }
  }
  if (state === 'QUEUED') return { words: 'Waiting to be read', tone: 'waiting' }
  if (state === 'CHUNKING' || state === 'OCR') {
    const total = file.read_pages_total
    return {
      words: total
        ? `Being read — ${file.read_pages_done} of ${total} ${total === 1 ? 'page' : 'pages'}`
        : 'Being read',
      tone: 'waiting',
    }
  }
  if (state === 'REVIEW_READY') return { words: 'Read', tone: 'good' }
  return { words: 'Not read yet', tone: 'waiting' }
}

/** True while a file's pages are still being turned into text. */
export const fileStillReading = (file: Pick<SourceFileView, 'read_state'>): boolean =>
  ['QUEUED', 'CHUNKING', 'OCR'].includes(file.read_state ?? '')

export const pagesWords = (pages: number | null | undefined): string =>
  pages ? `${pages} ${pages === 1 ? 'page' : 'pages'}` : ''

/* ----------------------------------------------------------------- progress */

export interface CourtReading {
  /** What is happening now, in plain words, without its closing mark: the
   *  page prints `…` while a job is live and a full stop when it is not. */
  headline: string
  /** `8 of 62 replies written`, or null before the paragraphs are counted. */
  count: string | null
  /** True while a job is still writing. Drives nothing but the wording. */
  live: boolean
  tone: 'working' | 'done' | 'stopped'
  /** True where the job is owed and nothing is running to write it. */
  unattended: boolean
}

const OWED: CourtDraftView['state'][] = ['queued', 'mapping', 'drafting', 'repairing']

/**
 * The drafting, as the strip above the replies reads.
 *
 * Six states, and each is a different thing to the officer:
 *
 * - `queued` — nothing has been sent yet. Said, because twelve minutes of
 *   "drafting" that has not started is a different wait from one that has.
 * - `mapping` — the pleading's paragraphs are being marked.
 * - `drafting` — the paragraphs being written **now**, read off the blocks the
 *   job itself marked (`draft_state: drafting`). Never worked out from a batch
 *   number and a guessed batch size: a range that is one paragraph out is a
 *   number this screen invented.
 * - `repairing` — the checks, and the one more attempt at what failed them.
 * - `complete` and `stopped` — the two ends. A stop carries the server's reason.
 *
 * And one that is not a state of the job: **the drafting service is not
 * running** (`service_unavailable`). The job is still owed, so the state still
 * reads `queued` or `drafting`, and nothing is writing it. "Drafting paragraphs
 * 9 to 16…" would then be a sentence about work nobody is doing, so the strip
 * says it is waiting for the service instead, and the page says the rest.
 */
export function readCourtProgress(
  court: CourtDraftView,
  blocks: Pick<BlockView, 'draft_state'>[] = [],
): CourtReading {
  const total = court.paragraphs_total ?? (blocks.length || null)
  const written = court.paragraphs_written ?? 0
  const count =
    total === null
      ? null
      : `${written} of ${total} ${total === 1 ? 'reply' : 'replies'} written`

  if (court.service_unavailable && OWED.includes(court.state)) {
    return {
      headline: 'Waiting for the drafting service',
      count,
      live: true,
      tone: 'stopped',
      unattended: true,
    }
  }

  switch (court.state) {
    case 'queued':
      return {
        headline: 'Waiting to start',
        count,
        live: true,
        tone: 'working',
        unattended: false,
      }
    case 'mapping':
      return {
        headline: 'Reading the pleading’s paragraphs',
        count,
        live: true,
        tone: 'working',
        unattended: false,
      }
    case 'drafting': {
      const now = blocks
        .map((block, index) => (block.draft_state === 'drafting' ? index + 1 : 0))
        .filter(Boolean)
      const first = now[0]
      const last = now[now.length - 1]
      const of = total === null ? '' : ` of ${total}`
      return {
        headline:
          first === undefined || last === undefined
            ? 'Drafting the replies'
            : first === last
              ? `Drafting paragraph ${first}${of}`
              : `Drafting paragraphs ${first} to ${last}${of}`,
        count,
        live: true,
        tone: 'working',
        unattended: false,
      }
    }
    case 'repairing':
      return {
        headline: 'Checking the replies',
        count,
        live: true,
        tone: 'working',
        unattended: false,
      }
    case 'complete':
      return {
        headline: 'Drafting is finished',
        count,
        live: false,
        tone: 'done',
        unattended: false,
      }
    case 'stopped':
      return {
        // The server's own reason, whole. Its closing full stop is the page's
        // to print, so a reason that carries one does not end in two.
        headline: `Stopped: ${(court.stopped_reason ?? '').trim().replace(/[.\s]+$/, '') || 'the drafting did not finish'}`,
        count,
        live: false,
        tone: 'stopped',
        unattended: false,
      }
    default:
      // `preflight` never reaches a version; said plainly if it ever does.
      return {
        headline: 'Not started',
        count,
        live: false,
        tone: 'working',
        unattended: false,
      }
  }
}

/* ------------------------------------------------- what the checks found */

/**
 * What the checks by code found, in the two kinds the office acts on
 * differently.
 *
 * - An **error** is wrong on its face — a date, a reference or a quotation that
 *   is in none of the papers. It went back for the one repair and still stands:
 *   *Check before filing*.
 * - A **note** is not a fault. It is something only the office can settle — a
 *   Hindi passage the reply quotes in English, to be read against the original:
 *   *Confirm against the original*.
 *
 * Printed alike, a translated quotation read as a failed reply, and four of
 * them on a sixty-two-paragraph draft read as four things wrong with it. The
 * level is the server's (`check_findings[].level`); nothing here decides it.
 *
 * `plain` is the earlier rendering, kept only for a reply that carries no
 * `check_findings`: its notes are then all there is, and they are shown as
 * things to check rather than dropped.
 */
export interface FindingsRead {
  errors: BlockFindingView[]
  notes: BlockFindingView[]
  plain: string[]
}

export function readFindings(
  block: Pick<BlockView, 'check_findings' | 'officer_notes'> | undefined,
): FindingsRead {
  const found = block?.check_findings ?? []
  if (!found.length) return { errors: [], notes: [], plain: block?.officer_notes ?? [] }
  return {
    errors: found.filter((finding) => finding.level !== 'note'),
    notes: found.filter((finding) => finding.level === 'note'),
    plain: [],
  }
}

export const FINDING_HEADS = {
  error: 'Check before filing',
  note: 'Confirm against the original',
} as const

/* -------------------------------------------------------- before drafting */

/**
 * How many paragraphs there are to answer, before they have been counted.
 *
 * The exact count exists only once the pleading has been cut into its
 * paragraphs, which happens after the officer presses the button. Until then
 * the server reads a rough count off the pleading's own numbering, and the
 * panel says it is rough — a figure shown bare would be taken as the count.
 * Null once the exact count is known, and where the server gave no estimate.
 */
export function estimateWords(
  court: Pick<CourtDraftView, 'paragraphs_total' | 'paragraphs_estimate'>,
): string | null {
  if (court.paragraphs_total) return null
  const about = court.paragraphs_estimate
  if (!about) return null
  return `about ${about} ${about === 1 ? 'paragraph' : 'paragraphs'} (estimated; exact once the pleading’s paragraphs have been read)`
}

/**
 * The pleading to send again to continue a draft that stopped: the one the
 * draft itself says it answers. Never guessed from the paragraph map or from
 * the order of the papers — a draft that does not say is one that cannot be
 * continued from here, and the screen says so.
 */
export const pleadingToContinue = (
  court: Pick<CourtDraftView, 'source_file_id'> | null | undefined,
): string | null => court?.source_file_id ?? null

/* ---------------------------------------------------------- what was sent */

/** How much of what can be sent in one go the papers come to, or null where
 *  the server gave no limit. Whole per cent, never below one for a real size. */
export function sizeWords(
  court: Pick<CourtDraftView, 'block_tokens' | 'block_token_limit'>,
): string | null {
  const size = court.block_tokens ?? 0
  const limit = court.block_token_limit ?? 0
  if (!limit || !size) return null
  const share = Math.max(1, Math.round((size / limit) * 100))
  return `The papers come to about ${share}% of the most that can be sent in one go.`
}

/**
 * Which copy of what the department filed earlier the draft was written
 * against. Three different footings, and the officer has to know which:
 *
 * - `uploaded` — the copy as filed, uploaded by the office. Its paragraph
 *   numbers are the ones a rejoinder cites.
 * - `signed` — the version the office signed in the app. The filed copy is not
 *   on the case, so the numbering may differ from what was filed.
 * - `draft` — the app's own draft, never signed. The weakest footing.
 *
 * The server says which (`CourtPaperView.standing`: `filed`, `signed`, `draft`)
 * and that is what is read. A draft made before the server recorded it carries
 * no `standing`; for those an uploaded copy is known from the paper's own kind,
 * and the app's own version reads as `draft` — the reading that asks the office
 * to upload the filed copy, never `signed`, which would claim an act nobody
 * recorded.
 */
export type FiledCopy = 'uploaded' | 'signed' | 'draft'

export function filedCopyUsed(
  paper: Pick<CourtPaperView, 'version_id' | 'source_file_id' | 'standing' | 'kind'>,
): FiledCopy | null {
  if (paper.standing === 'filed') return 'uploaded'
  if (paper.standing === 'signed' || paper.standing === 'draft') return paper.standing
  if (paper.version_id && !paper.source_file_id) return 'draft'
  if (paper.source_file_id && paper.kind === 'filed-document') return 'uploaded'
  return null
}

export const FILED_COPY_WORDS: Record<FiledCopy, string> = {
  uploaded: 'The copy as filed, uploaded by the office.',
  signed:
    'The version signed in the app. The copy as filed has not been uploaded, so its paragraph numbers may differ from the filed document.',
  draft:
    'The app’s own draft, not yet signed or filed. Upload the copy as filed so that later drafts keep to what the department actually said.',
}

/**
 * What one paper of the draft is, for the officer — never the sentence the
 * model was given, which is written for the model.
 *
 * Read off the paper itself: the kind it went into the draft as, and for a
 * Body or a file of Annexures the pleading it belongs to. Both are the
 * server's. A draft made before they were recorded says only *Case paper*.
 */
export function paperWords(
  paper: Pick<CourtPaperView, 'version_id' | 'source_file_id' | 'kind' | 'pleading'>,
): string {
  if (paper.version_id && !paper.source_file_id) return 'The department’s earlier reply'
  const kind = COURT_KINDS.find((k) => k.key === paper.kind)
  if (!kind) return 'Case paper'
  const pleading = PLEADINGS.find((p) => p.key === paper.pleading)
  return pleading ? `${kind.short} · ${pleading.label}` : kind.short
}

/** One page kept out of the draft, and why — the server's reason, kept whole. */
export function leftOutWords(entry: Pick<CourtLeftOutView, 'what' | 'page_no' | 'reason'>): {
  lead: string
  unreadable: boolean
} {
  const unreadable = entry.what !== 'copy'
  return {
    lead: unreadable
      ? `Page ${entry.page_no} could not be used`
      : `Page ${entry.page_no} is a copy of an earlier page`,
    unreadable,
  }
}

/** A server clause shown as a sentence: capital first, one full stop. */
export function asSentence(clause: string | null | undefined): string {
  const text = (clause ?? '').trim()
  if (!text) return ''
  const body = text.charAt(0).toUpperCase() + text.slice(1)
  return /[.!?]$/.test(body) ? body : `${body}.`
}
